import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale/pt-BR'
import { Bell, ShieldCheck } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useApprovals } from '@/lib/queries/approvals'
import { useActiveCompanyId } from '@/lib/queries/companies'
import { useNotificationsStore } from '@/stores/notifications'
import { cn } from '@/lib/utils'

/**
 * VEC-155 — NotificationsBell
 *
 * Sincroniza approvals pendentes com o store de notificações.
 * O bell no header mostra um badge com o count de não-lidas.
 * Ao abrir o popover, todas são marcadas como lidas.
 */
export function NotificationsBell() {
  const companyId = useActiveCompanyId()
  const { data: approvals = [] } = useApprovals(companyId, {
    refetchInterval: 15_000,
  })

  const { notifications, addNotification, markAllRead, clearOlderThan } =
    useNotificationsStore()

  // Limpa notificações com mais de 7 dias na montagem.
  const cleaned = useRef(false)
  useEffect(() => {
    if (!cleaned.current) {
      clearOlderThan(7 * 24 * 60 * 60 * 1000)
      cleaned.current = true
    }
  }, [clearOlderThan])

  // Sincroniza pending approvals → store de notificações.
  useEffect(() => {
    approvals
      .filter((a) => a.status === 'pending')
      .forEach((a) => {
        const typeLabels: Record<string, string> = {
          hire_agent: 'Nova contratação pendente',
          budget_increase: 'Solicitação de aumento de budget',
          strategy: 'Proposta estratégica para revisão',
          task_done: 'Tarefa concluída aguardando aprovação',
        }
        addNotification({
          id: `notif-${a.id}`,
          approvalId: a.id,
          message: typeLabels[a.requestType] ?? 'Nova aprovação pendente',
          href: '/council',
          createdAt: a.createdAt,
        })
      })
  }, [approvals, addNotification])

  const unreadCount = notifications.filter((n) => !n.read).length
  const recent = notifications.slice(0, 10)

  return (
    <Popover
      onOpenChange={(open) => {
        if (open) markAllRead()
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={
            unreadCount > 0
              ? `${unreadCount} notificação${unreadCount !== 1 ? 'ões' : ''} não lida${unreadCount !== 1 ? 's' : ''}`
              : 'Notificações'
          }
          className="relative"
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span
              className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground"
              aria-hidden="true"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-80 p-0">
        <div className="border-b border-border px-4 py-3">
          <p className="text-sm font-semibold">Notificações</p>
        </div>

        {recent.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <Bell className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              Nenhuma notificação ainda.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {recent.map((n) => (
              <li key={n.id}>
                <Link
                  to={n.href}
                  className={cn(
                    'flex items-start gap-3 px-4 py-3 text-sm transition-colors hover:bg-muted/40',
                    !n.read && 'bg-primary/5',
                  )}
                >
                  <ShieldCheck
                    className={cn(
                      'mt-0.5 h-4 w-4 shrink-0',
                      n.read ? 'text-muted-foreground' : 'text-primary',
                    )}
                    aria-hidden="true"
                  />
                  <div className="min-w-0 flex-1">
                    <p
                      className={cn(
                        'leading-snug',
                        !n.read && 'font-medium',
                      )}
                    >
                      {n.message}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(n.createdAt), {
                        addSuffix: true,
                        locale: ptBR,
                      })}
                    </p>
                  </div>
                  {!n.read && (
                    <span
                      className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary"
                      aria-hidden="true"
                    />
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}

        {recent.length > 0 && (
          <div className="border-t border-border px-4 py-2">
            <Link
              to="/council"
              className="text-xs text-primary hover:underline"
            >
              Ver todas as aprovações →
            </Link>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
